import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel,
  Alert, AlertIcon, Badge, Box, Button, Container, Flex, Heading, HStack, Image, Input, Link,
  SimpleGrid, Spinner, Text, VStack, Wrap, WrapItem,
} from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';
import { Navigate, useParams } from 'react-router-dom';
import { FiArrowRight, FiMapPin, FiPhone, FiTruck } from 'react-icons/fi';
import { track } from '../utils/analytics';

export const DIRECTORY_SLUG = 'wash-and-fold-near-me';

const PRODUCT_HOST = /smartlaundrybasket|localhost|127\.0\.0\.1|onrender\.com/;
const API = () => process.env.REACT_APP_AWS_API_URL || '';

function readEmbedded(slug) {
  try {
    const el = document.getElementById('directory-page-data');
    const data = el ? JSON.parse(el.textContent) : null;
    return data && data.slug === slug ? data : null;
  } catch (e) {
    return null;
  }
}

function trackClick(shop, zip) {
  track('directory_click', { listing_type: shop.kind === 'outside' ? 'outside' : 'platform', listing_id: shop.laundryId, zip });
  try {
    axios.post(`${API()}/api/directory/click`, { laundryId: shop.laundryId, zip });
  } catch (e) {}
}

function ShopCard({ shop, zip }) {
  return (
    <Flex direction="column" p={5} borderRadius="xl" border="1px solid" borderColor="gray.200" bg="white" boxShadow="sm" h="100%">
      <HStack spacing={3} align="center" mb={3}>
        {shop.logo
          ? <Image src={shop.logo} alt={shop.name} boxSize="48px" objectFit="contain" borderRadius="md" />
          : (
            <Flex boxSize="48px" borderRadius="md" bg="blue.500" color="white" align="center" justify="center" fontWeight="800">
              {shop.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
            </Flex>
          )}
        <Box flex="1" minW={0}>
          <Heading as="h3" fontSize="lg" noOfLines={2}>{shop.name}</Heading>
          {shop.washFoldPrice && <Text fontSize="sm" color="green.600" fontWeight="700">Wash &amp; fold from {shop.washFoldPrice}</Text>}
        </Box>
      </HStack>
      <Wrap spacing={2} mb={3}>
        {shop.deliversToZip && <WrapItem><Badge colorScheme="green">Delivers to {zip}</Badge></WrapItem>}
        {!shop.deliversToZip && shop.offersDelivery && <WrapItem><Badge colorScheme="blue">Pickup &amp; delivery</Badge></WrapItem>}
        {shop.acceptsDropoff && <WrapItem><Badge colorScheme="gray">Drop-off</Badge></WrapItem>}
        {shop.distanceMi != null && <WrapItem><Badge variant="outline">{shop.distanceMi} mi away</Badge></WrapItem>}
      </Wrap>
      <VStack align="stretch" spacing={1} fontSize="sm" color="gray.600" mb={4} flex="1">
        {shop.address && <HStack><FiMapPin /><Text>{shop.address}</Text></HStack>}
        {shop.phone && <HStack><FiPhone /><Link href={`tel:${shop.phone}`}>{shop.phone}</Link></HStack>}
      </VStack>
      <Button as="a" href={shop.orderUrl} onClick={() => trackClick(shop, zip)} colorScheme="blue" borderRadius="full"
        rightIcon={<FiArrowRight />}>
        {shop.deliversToZip ? 'Schedule a pickup' : 'Order now'}
      </Button>
    </Flex>
  );
}

function OutsideCard({ shop, zip }) {
  const [removed, setRemoved] = useState(false);
  const requestRemoval = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove the listing for ${shop.name}? It will be hidden right away.`)) return;
    try {
      await axios.post(`${API()}/api/directory/outside/${shop.listingId}/removal`, {});
      setRemoved(true);
    } catch (e) {}
  };
  if (removed) {
    return <Alert status="info" borderRadius="xl"><AlertIcon />The listing for {shop.name} has been removed.</Alert>;
  }
  return (
    <Flex direction="column" p={5} borderRadius="xl" border="1px dashed" borderColor="gray.300" bg="gray.50" h="100%">
      <Heading as="h3" fontSize="lg" mb={1} noOfLines={2}>{shop.name}</Heading>
      <Wrap spacing={2} mb={3}>
        {shop.deliversToZip && zip && <WrapItem><Badge colorScheme="green">Delivers to {zip}</Badge></WrapItem>}
        {!shop.deliversToZip && shop.offersDelivery && <WrapItem><Badge colorScheme="blue">Pickup &amp; delivery</Badge></WrapItem>}
        {shop.acceptsDropoff && <WrapItem><Badge colorScheme="gray">Drop-off</Badge></WrapItem>}
        {shop.distanceMi != null && <WrapItem><Badge variant="outline">{shop.distanceMi} mi away</Badge></WrapItem>}
      </Wrap>
      {shop.description && <Text fontSize="sm" color="gray.700" mb={2}>{shop.description}</Text>}
      {shop.services?.length > 0 && <Text fontSize="sm" color="gray.600" mb={2}>{shop.services.join(' · ')}</Text>}
      <VStack align="stretch" spacing={1} fontSize="sm" color="gray.600" mb={4} flex="1">
        {shop.address && <HStack><FiMapPin /><Text>{shop.address}</Text></HStack>}
      </VStack>
      <HStack spacing={2} mb={3}>
        {shop.phone && (
          <Button as="a" href={`tel:${shop.phone}`} onClick={() => trackClick(shop, zip)} size="sm" leftIcon={<FiPhone />}
            colorScheme="blue" variant="outline" borderRadius="full" flex="1">Call</Button>
        )}
        {shop.website && (
          <Button as="a" href={shop.website} target="_blank" rel="nofollow noopener noreferrer" onClick={() => trackClick(shop, zip)}
            size="sm" colorScheme="blue" variant="outline" borderRadius="full" flex="1">Website</Button>
        )}
      </HStack>
      <Text fontSize="xs" color="gray.500">
        Not on Smart Laundry Basket, so online ordering isn't available here. Listing based on public information.{' '}
        <Link href={shop.claimUrl} color="blue.600" fontWeight="600">Is this your laundromat? Claim it free</Link>
        {' · '}
        <Link as="button" onClick={requestRemoval} color="gray.500" textDecoration="underline">Request removal</Link>
      </Text>
    </Flex>
  );
}

function ListingCard({ shop, zip }) {
  return shop.kind === 'outside' ? <OutsideCard shop={shop} zip={zip} /> : <ShopCard shop={shop} zip={zip} />;
}

function Waitlist({ zip }) {
  const [contact, setContact] = useState('');
  const [state, setState] = useState({ sending: false, done: false, error: '' });

  const submit = async (e) => {
    e.preventDefault();
    const value = contact.trim();
    const body = value.includes('@') ? { zip, email: value } : { zip, phone: value };
    setState({ sending: true, done: false, error: '' });
    try {
      await axios.post(`${API()}/api/directory/waitlist`, body);
      track('directory_waitlist', { zip });
      setState({ sending: false, done: true, error: '' });
    } catch (err) {
      setState({ sending: false, done: false, error: err.response?.data?.detail || 'Something went wrong. Please try again.' });
    }
  };

  if (state.done) {
    return <Alert status="success" borderRadius="xl"><AlertIcon />Thanks! We'll let you know as soon as a laundromat near {zip} joins.</Alert>;
  }
  return (
    <Box p={6} borderRadius="xl" bg="blue.50" border="1px solid" borderColor="blue.100">
      <Heading as="h3" fontSize="lg" mb={1}>No listed laundromats serve {zip} yet</Heading>
      <Text color="gray.600" mb={4}>Leave your email or phone and we'll tell you when one does.</Text>
      <form onSubmit={submit}>
        <Flex gap={3} direction={{ base: 'column', sm: 'row' }}>
          <Input bg="white" placeholder="Email or phone" value={contact} onChange={(e) => setContact(e.target.value)} required />
          <Button type="submit" colorScheme="blue" isLoading={state.sending} px={8}>Notify me</Button>
        </Flex>
      </form>
      {state.error && <Text color="red.500" fontSize="sm" mt={2}>{state.error}</Text>}
    </Box>
  );
}

function ZipSearch({ initialZip = '' }) {
  const [zip, setZip] = useState(initialZip);
  const [searched, setSearched] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const search = async (e) => {
    if (e) e.preventDefault();
    const z = zip.trim();
    if (!/^\d{5}$/.test(z)) { setError('Enter a 5-digit ZIP code.'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API()}/api/directory/search`, { params: { zip: z } });
      setResults(data.results || []);
      setSearched(data.zip);
      track('directory_search', { zip: data.zip, result_count: (data.results || []).length });
    } catch (err) {
      setError(err.response?.data?.detail || 'Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <VStack spacing={6} align="stretch">
      <form onSubmit={search}>
        <Flex gap={3} maxW="520px" mx="auto" direction={{ base: 'column', sm: 'row' }}>
          <Input size="lg" bg="white" inputMode="numeric" maxLength={5} placeholder="Enter your ZIP code"
            value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, ''))} aria-label="ZIP code" />
          <Button type="submit" size="lg" colorScheme="blue" px={8} isLoading={loading}>Find laundromats</Button>
        </Flex>
      </form>
      {error && <Text color="red.500" textAlign="center">{error}</Text>}
      {searched && results.length > 0 && (
        <Box>
          <Text fontWeight="600" mb={3}>{results.length} laundromat{results.length !== 1 ? 's' : ''} near {searched}</Text>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
            {results.map((s) => <ListingCard key={s.laundryId} shop={s} zip={searched} />)}
          </SimpleGrid>
        </Box>
      )}
      {searched && results.length === 0 && <Waitlist zip={searched} />}
    </VStack>
  );
}

export default function DirectoryPage() {
  const { citySlug } = useParams();
  const slug = citySlug || DIRECTORY_SLUG;
  const [page, setPage] = useState(() => readEmbedded(slug));
  const [failed, setFailed] = useState(false);
  const isProductHost = PRODUCT_HOST.test(window.location.hostname.toLowerCase());

  useEffect(() => {
    if (!isProductHost || (page && page.slug === slug)) return;
    let active = true;
    axios.get(`${API()}/api/directory/page`, { params: { slug: citySlug || '' } })
      .then(({ data }) => { if (active) setPage(data); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [slug, citySlug, page, isProductHost]);

  if (!isProductHost) return <Navigate to="/" replace />;
  if (failed) return <Navigate to={`/${DIRECTORY_SLUG}`} replace />;
  if (!page) {
    return <Flex minH="60vh" align="center" justify="center"><Spinner size="lg" color="blue.500" /></Flex>;
  }

  const cityLinks = page.kind === 'hub' ? page.cities : page.nearby;

  return (
    <Box bg="white" minH="100vh">
      <Helmet>
        <title>{page.title}</title>
        <meta name="description" content={page.description} />
        <link rel="canonical" href={page.url} />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.description} />
        <meta property="og:url" content={page.url} />
      </Helmet>

      <Box as="nav" position="sticky" top={0} zIndex={100} bg="white" borderBottom="1px solid" borderColor="gray.100" boxShadow="sm">
        <Container maxW="1200px">
          <Flex py={3} align="center" justify="space-between">
            <Link href={`/${DIRECTORY_SLUG}`}><Image src="/slb-logo.png" alt="Smart Laundry Basket" h={{ base: '40px', md: '52px' }} objectFit="contain" /></Link>
            <Button as="a" href={`/${DIRECTORY_SLUG}/get-listed`} size="sm" variant="outline" colorScheme="blue" borderRadius="full">
              Own a laundromat? Get listed free
            </Button>
          </Flex>
        </Container>
      </Box>

      <Box bgGradient="linear(to-b, blue.50, white)" py={{ base: 12, md: 16 }}>
        <Container maxW="1100px">
          <VStack spacing={4} textAlign="center" mb={8}>
            <HStack color="blue.600" fontWeight="700" fontSize="sm"><FiTruck /><Text>Wash · Dry · Fold · Pickup &amp; delivery</Text></HStack>
            <Heading as="h1" fontSize={{ base: '3xl', md: '5xl' }} lineHeight="shorter" fontWeight="800">{page.h1}</Heading>
            <Text fontSize={{ base: 'md', md: 'xl' }} color="gray.600" maxW="760px">{page.intro}</Text>
          </VStack>
          <ZipSearch />
        </Container>
      </Box>

      {page.kind === 'city' && page.shops?.length > 0 && (
        <Box as="section" py={{ base: 8, md: 12 }}>
          <Container maxW="1100px">
            <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} mb={5}>Laundromats serving {page.city}</Heading>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
              {page.shops.map((s) => <ListingCard key={s.laundryId} shop={{ ...s, deliversToZip: false }} zip="" />)}
            </SimpleGrid>
          </Container>
        </Box>
      )}

      {cityLinks?.length > 0 && (
        <Box as="section" py={{ base: 8, md: 10 }} bg="gray.50">
          <Container maxW="1100px">
            <Heading as="h2" fontSize={{ base: 'lg', md: 'xl' }} mb={4}>{page.kind === 'hub' ? 'Cities we cover' : 'Nearby cities'}</Heading>
            <Wrap spacing={3}>
              {cityLinks.map((c) => (
                <WrapItem key={c.slug}>
                  <Button as="a" href={`/${DIRECTORY_SLUG}/${c.slug}`} size="sm" variant="outline" borderRadius="full">
                    {c.city}, {c.state}
                  </Button>
                </WrapItem>
              ))}
            </Wrap>
          </Container>
        </Box>
      )}

      <Box as="section" py={{ base: 10, md: 14 }}>
        <Container maxW="760px">
          <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} mb={6} textAlign="center">Frequently asked questions</Heading>
          <Accordion allowMultiple>
            {page.faqs.map((f) => (
              <AccordionItem key={f.q} border="none" mb={3}>
                <AccordionButton bg="gray.50" borderRadius="xl" py={4} px={6} _hover={{ bg: 'gray.100' }} _expanded={{ bg: 'blue.50' }}>
                  <Text as="h3" flex="1" textAlign="left" fontWeight="600">{f.q}</Text>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel py={4} px={6} color="gray.600" fontSize="sm" lineHeight="tall">{f.a}</AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Container>
      </Box>

      <Box py={{ base: 12, md: 14 }} bg="blue.600" color="white" textAlign="center">
        <Container maxW="760px">
          <VStack spacing={4}>
            <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }}>Own a laundromat?</Heading>
            <Text opacity={0.9}>Get listed here and reach wash &amp; fold customers in your area. Free, with no software or contract required.</Text>
            <Button as="a" href={`/${DIRECTORY_SLUG}/get-listed`} size="lg" bg="white" color="blue.600" borderRadius="full" px={8}
              _hover={{ bg: 'gray.100' }} rightIcon={<FiArrowRight />}>
              List My Laundromat Free
            </Button>
            <Text fontSize="sm" opacity={0.85}>
              Want online ordering, pickup &amp; delivery and a POS too?{' '}
              <Link href="/quickstart" textDecoration="underline">Try Smart Laundry Basket free</Link>
            </Text>
          </VStack>
        </Container>
      </Box>

      <Box py={6} bg="gray.900" color="gray.400" textAlign="center" fontSize="xs">
        &copy; {new Date().getFullYear()} Smart Laundry Basket · <Link href="/slb">For laundromats</Link>
        {' · '}<Link href="/privacy-policy">Privacy</Link>{' · '}<Link href="/terms">Terms</Link>
      </Box>
    </Box>
  );
}
